import { SoftTagTableSkeleton } from '@/components/skeleton';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Table, AhuaIcon } from '@yaradigitallabs/ahua-react';
import { useEffect, useState } from 'react';
import type { Tags } from '../types/tags.types';
import { Colors } from '../../../../public/GlobalTokens';

interface TagsTableProps {
  hasActiveFilter: boolean;
  columns: any;
  tags: Tags[];
  loading: boolean;
}

export const TagsTable = ({
  hasActiveFilter,
  columns,
  tags,
  loading,
}: TagsTableProps) => {
  const [tagsData, setTagsData] = useState<Tags[]>([]); // Table data
  const [sorting, setSorting] = useState<SortingState>([]); // Sorting state
  const [rowSelection, setRowSelection] = useState({}); // Row selection state

  useEffect(() => {
    setTagsData(tags);
  }, [tags]);

  // Setup TanStack Table instance for advanced table features
  const table = useReactTable({
    data: tagsData,
    columns,
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <div className="w-full">
      {/* Show skeleton while loading */}
      {loading ? (
        <SoftTagTableSkeleton />
      ) : (
        <Table css={{ width: '100%' }}>
          <thead>
            {/* Render table headers with sorting */}
            {table.getHeaderGroups().map(headerGroup => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Table.SortHead
                    css={{ paddingTop: '4px', paddingBottom: '4px' }}
                    direction={header.column.getIsSorted()}
                    active={Boolean(header.column.getIsSorted())}
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className="pl-3 pr-2 border-t border-r border-l border-[#E5E7EB]"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.SortHead>
                ))}
              </Table.Row>
            ))}
          </thead>
          <tbody>
            {/* Empty state if no tags found */}
            {tagsData.length === 0 ? (
              <Table.Row>
                <Table.Cell
                  colSpan={columns.length}
                  className="text-center py-16 border-r border-[#E5E7EB]"
                >
                  <div className="flex flex-col items-center gap-4">
                    <AhuaIcon
                      icon="Search"
                      style={{ stroke: Colors.colorType.neutral.contrast }}
                      className="w-12 h-12"
                    />
                    <div className="text-center">
                      <p
                        className="text-lg font-medium mb-2"
                        style={{ color: Colors.colorType.neutral.contrast }}
                      >
                        {hasActiveFilter
                          ? 'No tags match your filters'
                          : 'No tags found'}
                      </p>
                      {hasActiveFilter && (
                        <p
                          className="text-sm"
                          style={{ color: Colors.colorType.neutral.contrast }}
                        >
                          Try adjusting your filter criteria or reset filters
                        </p>
                      )}
                    </div>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              // Render table rows and cells
              table.getRowModel().rows.map(row => (
                <Table.Row key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <Table.Cell
                      css={{ paddingTop: '8px', paddingBottom: '8px' }}
                      key={cell.id}
                      className="px-4 py-0 "
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
};
